import { namespace, resolve } from 'wsm-tagging-utils';

const runComponent = (win) => {
	namespace(win, 'DDC.tracking.framework.config');

	const {
		DDC: { tracking },
		com
	} = win;

	/**
	 * Constructs a new Component representation given the specified definition
	 * and default definition. A component definition will have the following
	 * structure:
	 *
	 * var definition = {
	 *     className: '<fully qualified class name>',
	 *     properties: {
	 *         propertyA: 'value',
	 *         propertyB: 12345,
	 *         propertyC: true,
	 *     },
	 *     initializer: '<name of function to call after property injection>'
	 * };
	 *
	 * @param {Object} definition The primary component definition
	 * @param {Object|undefined} defaults The fallback definition used when not present in primary definition
	 * @constructor
	 */
	tracking.framework.config.Component = function Component(
		definition,
		defaults
	) {
		this.log = new com.dealer.log.Logger('ws-tagging:Component');
		this.injector = tracking.framework.config.Injector.get();
		this.definition = definition || {};
		this.defaults = defaults || {};
	};

	tracking.framework.config.Component.prototype = {
		/**
		 * The default constructor
		 */
		constructor: win.DDC.tracking.framework.config.Component,

		/**
		 * Applies the component definition to this current instance.
		 *
		 * @param {Object} definition The primary component definition
		 * @param {Object|undefined} defaults The fallback definition used when not present in primary definition
		 * @return {tracking.framework.config.Component}
		 */
		withDefinition(definition, defaults) {
			this.definition = definition || {};
			this.defaults = defaults || {};
			return this;
		},

		/**
		 * Returns the configured fully qualified class name for the component represented by
		 * the current definition. This is the only required component definition property.
		 *
		 * @return {string} The fully qualified class name for the current component
		 */
		className() {
			if (this.definition.className !== undefined) {
				return this.definition.className;
			}
			if (this.defaults.className !== undefined) {
				return this.defaults.className;
			}
			throw new Error(
				'Failed to determine class from component definition'
			);
		},

		/**
		 * Returns an optional object containing the property names and associated
		 * values to inject into the component after instantiation.
		 *
		 * @return {Object|undefined} The optional object containing property names and values
		 */
		properties() {
			return this.definition.properties || this.defaults.properties;
			// if (this.definition.properties !== undefined) {
			// 	return this.definition.properties;
			// }
			// if (this.defaults.properties !== undefined) {
			// 	return this.defaults.properties;
			// }
		},

		/**
		 * Returns the optional name of an initializer function to invoke after component
		 * has been instantiated and properties set.
		 *
		 * @return {string|undefined} The optional name of an initializer function to invoke
		 */
		initializer() {
			return this.definition.initializer || this.defaults.initializer;
			// if (this.definition.initializer !== undefined) {
			// 	return this.definition.initializer;
			// }
			// if (this.defaults.initializer !== undefined) {
			// 	return this.defaults.initializer;
			// }
		},

		/**
		 * Instantiates a component instance based on it's current definition. Component
		 * instantiation observes the following protocol:
		 *
		 * 1. {@link #className} is used to determine the implementation class whose
		 *    constructor function is to be resolved. The factory function will be invoked
		 *    with this constructor function and an instance of the class will be returned.
		 *
		 * 2. The component instance returned from the factory function will be injected
		 *    with the results of {@link #properties}, if any configured properties are present.
		 *
		 * 3. The component instance's initializer will be invoked, if present.
		 *
		 * @param {Object} caller The caller, used as the 'this' argument when invoking
		 *    the factory function
		 * @param {Function} factory A factory function that will return a new instance
		 *    when invoked with the resolved constructor function.
		 * @param {boolean|undefined} initializerRequired An optional flag indicating the
		 *    required presence of the initializer function; defaults to false.
		 */
		instantiate(caller, factory, initializerRequired = false) {
			// initializerRequired =
			// 	initializerRequired === undefined ? false : initializerRequired;
			const className = this.className();
			this.log.debug(`instantiating ${className}`);
			// Instantiate the component
			const instance = factory.call(caller, resolve(win, className));
			// Inject the properties, if present
			const properties = this.properties();
			if (properties !== undefined) {
				this.log.debug(
					`injecting configured properties into ${className}`
				);
				this.injector.setProperties(instance, properties);
			}
			// Initialize the component
			const initializer = this.initializer();
			if (initializerRequired || initializer !== undefined) {
				this.log.debug(
					`attempting to invoke ${className}.${initializer}()`
				);
				this.injector.initialize(
					instance,
					initializer,
					initializerRequired
				);
			}
			return instance;
		}
	};
};

export default runComponent;
